import React, { useEffect, useState } from 'react';
import {
    BookOpen, FileText, Download, Upload, X, Loader2, AlertTriangle, Info
} from 'lucide-react';
import { authorAuthService, PendingRevisionRequestDetail } from '../services/authorAuth';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl } from '../../../utils/downloadHelper';
import { useNotification } from '../../sharedComponents/Notifications';

interface Props {
    submissionId: string;
    onSuccess: () => void;
}

const RevisedCopyDetails: React.FC<Props> = ({ submissionId, onSuccess }) => {
    const [detail, setDetail] = useState<PendingRevisionRequestDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [comment, setComment] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [downloadingOriginal, setDownloadingOriginal] = useState(false);
    const [downloadingReviewed, setDownloadingReviewed] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { showNotification } = useNotification();

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await authorAuthService.getPendingRevisionRequestDetail(submissionId);
                if (res.isSuccess && res.data) {
                    setDetail(res.data);
                } else {
                    setError(res.message || 'Failed to load revision request details.');
                }
            } catch {
                showNotification('error', {
                    title: 'Error',
                    message: 'An unexpected error occurred while loading revision details.',
                });
                setError('An unexpected error occurred.');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [submissionId]);

    const resolveUrl = (url: string) =>
        url.startsWith('http') ? url : `${FILE_BASE_URL}/${url}`;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.name.match(/\.(doc|docx)$/i)) {
            showNotification('error', {
                title: 'Invalid File Type',
                message: 'Only .doc or .docx files are accepted.',
            });
            return;
        }
        if (f.size > 20 * 1024 * 1024) {
            showNotification('error', {
                title: 'File Too Large',
                message: 'File exceeds the 20MB limit.',
            });
            return;
        }
        setFile(f);
    };

    const handleSubmit = async () => {
        if (!file || !detail) return;
        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('SubmissionId', detail.submissionId);
            formData.append('Comment', comment.trim());
            formData.append('RevisedFile', file);

            const res = await authorAuthService.submitRevisedCopyManuscript(formData);
            if (res.isSuccess) {
                showNotification('success', {
                    title: 'Success',
                    message: res.message || 'Revised copy submitted successfully.',
                });
                onSuccess();
            } else {
                showNotification('error', {
                    title: 'Submission Failed',
                    message: res.message || 'Submission failed. Please try again.',
                });
            }
        } catch {
            showNotification('error', {
                title: 'Error',
                message: 'An unexpected error occurred during submission.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center py-24">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
        </div>
    );

    if (error || !detail) return (
        <div className="flex flex-col items-center justify-center py-20 gap-3 text-center px-6">
            <AlertTriangle className="w-8 h-8 text-red-400" />
            <p className="text-gray-500 text-sm">{error || 'Unable to load revision details.'}</p>
        </div>
    );

    const authors = detail.bookAuthors.length > 0
        ? detail.bookAuthors.map(a => a.name).join(', ')
        : '—';

    return (
        <div className="space-y-6 pb-10">

            {/* ── Submission Info ─────────────────────────────────────── */}
            <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-[#088A61]" />
                    Submission Details
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 space-y-3 border border-gray-100">
                    <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Title</p>
                        <p className="text-sm font-semibold text-gray-900">{detail.submissionTitle}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Submission No.</p>
                            <p className="text-sm text-gray-700 font-mono">{detail.submissionNumber}</p>
                        </div>
                        <div>
                            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Status</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                                {detail.status}
                            </span>
                        </div>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Authors</p>
                        <p className="text-sm text-gray-700">{authors}</p>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-0.5">Requested On</p>
                        <p className="text-sm text-gray-700">
                            {new Date(detail.requestedAt).toLocaleDateString('en-GB', {
                                day: '2-digit', month: 'short', year: 'numeric'
                            })}
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Admin Comment ───────────────────────────────────────── */}
            {detail.comment && (
                <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <Info className="w-4 h-4 text-blue-500" />
                        Revision Instructions from Admin
                    </h3>
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                        <p className="text-sm text-gray-700 leading-relaxed">{detail.comment}</p>
                    </div>
                </div>
            )}

            {/* ── Documents ───────────────────────────────────────────── */}
            <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#088A61]" />
                    Documents
                </h3>
                <div className="space-y-3">

                    {/* Your original submission version */}
                    {detail.basedOnVersionUrl && (
                        <div className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 rounded-lg">
                                    <FileText className="w-4 h-4 text-gray-500" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Your Submitted Version</p>
                                    <p className="text-xs text-gray-400">The version that was reviewed</p>
                                </div>
                            </div>
                            <button
                                disabled={downloadingOriginal}
                                onClick={async () => {
                                    setDownloadingOriginal(true);
                                    try {
                                        await downloadFileFromUrl(
                                            resolveUrl(detail.basedOnVersionUrl),
                                            `${detail.submissionNumber}_submitted.docx`
                                        );
                                    } finally {
                                        setDownloadingOriginal(false);
                                    }
                                }}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
                            >
                                {downloadingOriginal
                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                    : <><Download className="w-3 h-3" />Download</>
                                }
                            </button>
                        </div>
                    )}

                    {/* Reviewed manuscript with corrections */}
                    {detail.reviewedManuscriptUrl && (
                        <div className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-xl p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-100 rounded-lg">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-gray-900">Reviewed Manuscript</p>
                                    <p className="text-xs text-blue-500">Annotated with corrections by reviewer</p>
                                </div>
                            </div>
                            <button
                                disabled={downloadingReviewed}
                                onClick={async () => {
                                    setDownloadingReviewed(true);
                                    try {
                                        await downloadFileFromUrl(
                                            resolveUrl(detail.reviewedManuscriptUrl),
                                            `${detail.submissionNumber}_reviewed.docx`
                                        );
                                    } finally {
                                        setDownloadingReviewed(false);
                                    }
                                }}
                                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-white border border-blue-200 rounded-lg text-blue-700 hover:bg-blue-50 transition-colors disabled:opacity-60"
                            >
                                {downloadingReviewed
                                    ? <><Loader2 className="w-3 h-3 animate-spin" />Downloading...</>
                                    : <><Download className="w-3 h-3" />Download</>
                                }
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Submission Instructions Banner ──────────────────────── */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm font-semibold text-amber-800">Before submitting your revised copy</p>
                </div>
                <ul className="text-xs text-amber-700 space-y-1.5 ml-6 list-disc">
                    <li>Download the <span className="font-semibold">Reviewed Manuscript</span> above and carefully apply all corrections and annotations made by the reviewer.</li>
                    <li>Ensure your revised file includes the following metadata at the beginning of the document:
                        <ul className="mt-1 ml-4 space-y-0.5 list-disc text-amber-600">
                            <li>Manuscript title</li>
                            <li>Full author name(s) and affiliations</li>
                            <li>Table of contents</li>
                            <li>Book/manuscript abstract</li>
                        </ul>
                    </li>
                    <li>Only <span className="font-semibold">.doc or .docx</span> files are accepted (max 20MB).</li>
                </ul>
            </div>

            {/* ── Submit Revised Copy ─────────────────────────────────── */}
            <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900">Submit Your Revised Copy</h3>

                <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 block">
                        Comment <span className="text-gray-400 font-normal normal-case">(optional)</span>
                    </label>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={3}
                        placeholder="Describe the changes you made in this revision..."
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#088A61] focus:ring-1 focus:ring-[#088A61]/20 resize-none transition-colors"
                    />
                </div>

                <div
                    onClick={() => document.getElementById('revised-copy-input')?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-[#088A61] hover:bg-[#088A61]/5 transition-all"
                >
                    <input
                        type="file"
                        id="revised-copy-input"
                        className="hidden"
                        accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={handleFileChange}
                    />
                    <div className="mb-3 flex justify-center">
                        <div className="w-12 h-12 bg-[#088A61]/10 rounded-full flex items-center justify-center">
                            <Upload className="w-6 h-6 text-[#088A61]" />
                        </div>
                    </div>
                    <p className="text-gray-900 font-semibold mb-1">Click to upload revised manuscript</p>
                    <p className="text-xs text-gray-400">DOC / DOCX only · max 20MB</p>
                </div>

                {file && (
                    <div className="bg-[#088A61]/5 rounded-xl p-4 border border-[#088A61]/15 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <FileText className="w-4 h-4 text-[#088A61]" />
                            <div>
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-400">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setFile(null)}
                            className="w-8 h-8 rounded-lg flex items-center justify-center bg-white border border-red-100 text-red-400 hover:bg-red-50 transition-colors"
                        >
                            <X size={15} />
                        </button>
                    </div>
                )}

                <button
                    onClick={handleSubmit}
                    disabled={!file || isSubmitting}
                    className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 text-sm
                        ${!file || isSubmitting
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-[#088A61] hover:bg-[#066e4d] text-white cursor-pointer shadow-sm'
                        }`}
                >
                    {isSubmitting
                        ? <><Loader2 className="w-4 h-4 animate-spin" />Submitting...</>
                        : <><Upload size={16} />Submit Revised Copy</>
                    }
                </button>
            </div>
        </div>
    );
};

export default RevisedCopyDetails;